import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    selectRegisterBrowserModal,
    selectUserBrowserInfo,
    toggleBrowserLimitsModal,
} from 'src/modules/profile/user/services/user-profile-slice';
import {
    getBrowserRegistrationCode,
} from 'src/openapi/admin/apis/GetBrowserRegistrationCodeApi';
import {getUserBrowserInfo} from 'src/openapi/admin/apis/GetUserBrowserInfoApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';

import type {MessageContextType} from 'src/contexts/message-context';

type Props = {
    userId: number,
};

const GenerateRegisterBrowserCode = ({userId, onClose}: {userId: number, onClose: () => void}) => {
    const [code, setCode] = useState<string>();
    const [loading, setLoading] = useState<boolean>();

    const {setSuccessMessage} = useContext<MessageContextType>(MessageContext);

    const apiDispatch = useAppDispatch();

    useEffect(() => {
        const getCode = async () => {
            setLoading(true);
            const res = await apiDispatch(getBrowserRegistrationCode({userId}));
            setLoading(false);

            if (res.payload.success) {
                setCode(res.payload.data.value);
            }
        };

        getCode();
    }, [userId, apiDispatch]);

    const onCopy = useCallback(() => {
        navigator.clipboard.writeText(`${code}`).then(() => {
            onClose();
            setSuccessMessage('Browser Registration Code is copied to your clipboard');
        });
    }, [code, onClose, setSuccessMessage]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Generate Registration Code
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {(!loading && code) ? <span className="enable-selection">Registration Code: {code}</span> : null}
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button disabled={!code} variant="Type.PRIMARY" onClick={onCopy}>Copy</Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>Cancel</Button>
                </Group>
            </DialogFooter>
        </>
    );
};

export const GenerateRegistrationCodeController = ({userId}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const {hasAccess} = useAdminAccessRight();
    const showRegisterBrowserModal = useSelector(selectRegisterBrowserModal);
    const browserInfo = useSelector(selectUserBrowserInfo);
    const {userFieldChange} = useEventTracking();

    useEffect(() => {
        if (!browserInfo) {
            apiDispatch(getUserBrowserInfo({userId}));
        }
    }, [userId, browserInfo, apiDispatch]);

    const onClose = useCallback(() => {
        dispatch(toggleBrowserLimitsModal(false));
    }, [dispatch]);

    const onToggle = useCallback(() => {
        userFieldChange({
            viewType: 'Existed',
            section: 'Browser Registration',
            parameterName: 'Generate Registration Code',
        });
        dispatch(toggleBrowserLimitsModal(true));
    }, [dispatch, userFieldChange]);

    const disabled = !browserInfo?.enabled || !hasAccess(ACCESS_RIGHTS.USER_SCREEN_EDIT);

    return (
        <>
            <Button variant="Type.PRIMARY" disabled={disabled} onClick={onToggle}>
                Generate Registration Code
            </Button>
            <Dialog
                shown={showRegisterBrowserModal}
                setShown={onClose}
                wrapperProps={{style: {width: 420}}}>
                <GenerateRegisterBrowserCode userId={userId} onClose={onClose}/>
            </Dialog>
        </>
    );
};
